/**
 * Dentis Discount Engine
 *
 * Resolves an active storefront discount code via Shopify's own cart machinery
 * and exposes it as `window.DentisActiveDiscount` plus a `dentis:discount-resolved`
 * DOM event so price snippets and the promo-status block can react.
 *
 * Source of truth: Shopify (cart.discount_applications + line_item.discount_allocations).
 * We never re-implement discount math; we mirror what Shopify computes.
 *
 * Failure mode: silent. On any error, `window.DentisActiveDiscount` is set to
 * a benign null state, the resolved event still fires (with `active: false`),
 * and price rendering falls back to the normal Liquid-rendered markup.
 */
(function () {
  'use strict';

  if (!window.DentisDebug) {
    var debugFlag = false;
    try {
      var qs = new URLSearchParams(window.location.search || '');
      if (qs.get('dentis_debug') === '1' || qs.get('dentis_debug') === 'true') debugFlag = true;
    } catch (e) { /* no-op */ }
    try {
      if (window.localStorage && window.localStorage.getItem('dentis:debug') === '1') debugFlag = true;
    } catch (e) { /* no-op */ }
    var noop = function () {};
    var T0 = (function () { try { return performance.now(); } catch (e) { return Date.now(); } })();
    var SEQ = 0;
    function nowDelta() {
      try { return Math.round(performance.now() - T0); } catch (e) { return Date.now() - T0; }
    }
    function pad(n, len) { var s = String(n); while (s.length < len) s = ' ' + s; return s; }
    function pad0(n, len) { var s = String(n); while (s.length < len) s = '0' + s; return s; }
    // Structured trace: every entry gets a monotonic seq #, a relative-to-init
    // timestamp, and a phase tag. Output looks like:
    //
    //   [Dentis #001    12ms ENG-INIT] engine boot {debug:true}
    //   [Dentis #002    45ms RESOLVE  ] cookieCode=18MAI-... picked=18MAI-...
    //   [Dentis #003    51ms P1-CART  ] discount_codes=[{...}] item_count=0
    //
    // Use this to correlate engine events with paint events (which carry the
    // same seq numbers via window.DentisDebug.seq()).
    function trace(level, phase, msg, payload) {
      SEQ++;
      var prefix = '[Dentis #' + pad0(SEQ, 3) + ' ' + pad(nowDelta(), 5) + 'ms ' + pad(phase, 9) + ']';
      var color = level === 'error' ? '#cc0000' : (level === 'warn' ? '#f5a524' : '#0e01ab');
      var args = ['%c' + prefix, 'color:' + color + ';font-weight:bold;', msg];
      if (payload !== undefined) args.push(payload);
      try {
        if (level === 'error') { console.error.apply(console, args); }
        else if (level === 'warn') { console.warn.apply(console, args); }
        else { console.log.apply(console, args); }
      } catch (e) { /* swallow */ }
    }
    window.DentisDebug = {
      enabled: debugFlag,
      log: debugFlag ? (console.log.bind(console, '%c[DentisDiscount]', 'color:#0e01ab;font-weight:bold;')) : noop,
      group: debugFlag ? (console.groupCollapsed ? console.groupCollapsed.bind(console) : console.log.bind(console)) : noop,
      groupEnd: debugFlag ? (console.groupEnd ? console.groupEnd.bind(console) : noop) : noop,
      warn: debugFlag ? (console.warn.bind(console, '%c[DentisDiscount]', 'color:#f5a524;font-weight:bold;')) : noop,
      error: debugFlag ? (console.error.bind(console, '%c[DentisDiscount]', 'color:#cc0000;font-weight:bold;')) : noop,
      trace: debugFlag ? function (phase, msg, payload) { trace('log', phase, msg, payload); } : noop,
      traceWarn: debugFlag ? function (phase, msg, payload) { trace('warn', phase, msg, payload); } : noop,
      traceError: function (phase, msg, payload) { trace('error', phase, msg, payload); },
      seq: function () { return SEQ; }
    };
  }
  var DBG = window.DentisDebug;
  // Local shortcut so the rest of the file can write DBG.t('PHASE', 'msg', {...})
  // instead of DBG.trace('PHASE', 'msg', {...}). Same for warn/error variants.
  DBG.t = DBG.trace;
  DBG.tw = DBG.traceWarn;
  DBG.te = DBG.traceError;

  if (window.DentisDiscountEngine) {
    DBG.log('engine already initialized, skipping re-init');
    return;
  }

  var STORAGE_KEY = 'dentis:active-discount';
  var STORAGE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  var COOKIE_NAME = 'discount_code';
  var URL_PARAMS = ['discount', 'discount_code'];
  var EVENT_NAME = 'dentis:discount-resolved';

  // ────────────────────────────────────────────────────────────────────────
  // Combinability module
  //
  // Mirrors Shopify Admin's "Combines with" toggles. Every discount carries
  // a `kind` ('product' | 'order') and a `combines: {order, product}` shape.
  //
  // The matrix (from real-world Shopify behavior, NOT a textbook reading of
  // the docs):
  //
  //   1. Within a single kind (product-vs-product or order-vs-order):
  //        - All discounts with combines[kind]=true ("permissive") apply
  //          unconditionally.
  //        - Among discounts with combines[kind]=false ("exclusive"), only
  //          the one with the largest savings applies; the rest are dropped.
  //
  //   2. Cross-kind (product-vs-order):
  //        - The pair coexists iff BOTH sides allow the other side's kind.
  //          I.e. product.combines.order AND order.combines.product. If
  //          either is false, the order-side discount is dropped (since
  //          product-side is typically the "primary" customer-paying line).
  //
  // The default for any missing combines value is true (permissive), so
  // older JSON entries without combines blocks behave like permissive
  // discounts (no behavior change for them).
  // ────────────────────────────────────────────────────────────────────────
  var Combinability = (function () {
    function normalize(c) {
      var src = (c && c.combines) || {};
      return {
        order: src.order !== false,    // default true
        product: src.product !== false // default true
      };
    }
    function kindOf(d) {
      // Storewide single is order-kind by convention (hardcoded). Everything
      // else in the campaigns JSON is product-kind.
      return (d && d.kind) === 'order' ? 'order' : 'product';
    }
    return { normalize: normalize, kindOf: kindOf };
  })();

  // Influencer auto-recognition. Codes following the convention
  // `<LETTERS>20` (case-insensitive) are treated as order-level 20%-off
  // discounts that combine with everything. The pattern is intentionally
  // tight: contiguous A–Z only, ending with the literal digits `20`. This
  // rejects campaign codes that happen to contain "20" (e.g.
  // `18MAI-1200KR-GAVEKORT-DUOPAKKEN`, `LINE-20`, `LINE20A`).
  var INFLUENCER_PATTERN = /^[A-Z]+20$/i;
  var INFLUENCER_DEFAULT_PERCENTAGE = 20;
  function isInfluencerCode(code) {
    if (!code) return false;
    return INFLUENCER_PATTERN.test(String(code).trim());
  }

  // ────────────────────────────────────────────────────────────────────────
  // Eevy bundle campaign ingestion
  //
  // An active Eevy bundle campaign (window.EevyBundleConfigBase.campaigns) is a
  // first-class native discount source: it is normalized into the SAME campaign
  // shape readStaticPromoConfig() returns for DentisStaticPromoConfig campaigns,
  // so it flows through the identical resolve -> match -> synthesize -> filter
  // -> render path as every other discount and survives re-renders by being
  // recomputed on every resolve() (rather than raced in afterwards).
  //
  // Two markers distinguish it downstream WITHOUT special-casing the engine:
  //   - eevy_bundle:true     -> filterByCartTruth trusts it from config (the
  //                             Eevy offer is config-driven, same rationale as
  //                             cores; the gid-managed Shopify discount may not
  //                             surface in cart.discount_codes on an empty PDP
  //                             cart).
  //   - display_covered:true -> price-override never paints a price node for it
  //                             (Eevy already owns the displayed bundle price),
  //                             and promo-status derives the % from the bundle
  //                             offer instead of multiplying the displayed
  //                             price. kind:'product' keeps it out of the
  //                             picker/promo order-discount headline composer.
  //
  // Savings numbers are OPTIONAL. Prefer a real display_price_cents delta
  // (offer_price_cents / discount_cents via buildEffectiveBundleConfig). When
  // the campaign is component-level only (roles free/fixed/percent with
  // discount_amount_cents / discount_pct, no top-level display delta) derive
  // savings from component economics. When neither yields a number, the
  // campaign is still ingested so message/badge/spart_message can render —
  // never invent a % from the code shape (see a9401f0 influencer guard).
  // ────────────────────────────────────────────────────────────────────────
  function eevyCampaignDiscountPercent(rt, base, campaign) {
    try {
      if (!rt || typeof rt.buildEffectiveBundleConfig !== 'function') return 0;
      var eff = rt.buildEffectiveBundleConfig(base, campaign);
      var baseTiers = (base && base.tiers) || {};
      var effTiers = (eff && eff.tiers) || {};
      var maxPct = 0;
      for (var k in baseTiers) {
        if (!Object.prototype.hasOwnProperty.call(baseTiers, k)) continue;
        var baseC = Number(baseTiers[k] && baseTiers[k].display_price_cents);
        var effC = Number(effTiers[k] && effTiers[k].display_price_cents);
        if (!isFinite(baseC) || baseC <= 0 || !isFinite(effC)) continue;
        if (effC < baseC) {
          var pct = Math.round((baseC - effC) / baseC * 100);
          if (pct > maxPct) maxPct = pct;
        }
      }
      return maxPct;
    } catch (e) { return 0; }
  }

  // Per-component final for one bundle (qty of the component line as authored).
  // Mirrors dentis-cart-campaign.js childFinalCents role economics so savings
  // text agrees with cart-drawer component math.
  function eevyComponentFinalCents(items, comp) {
    if (!comp) return null;
    var item = items && items[comp.item];
    if (!item) return null;
    var unit = Number(item.unit_price_cents) || 0;
    var qty = Number(comp.qty) || 1;
    if (!(qty > 0)) qty = 1;
    if (comp.role === 'free') return 0;
    if (comp.role === 'fixed' && comp.discount_amount_cents != null) {
      var disc = (Number(comp.discount_amount_cents) || 0) * qty;
      return Math.max(0, unit * qty - disc);
    }
    if (comp.discount_pct != null && Number(comp.discount_pct) > 0) {
      return Math.floor(unit * qty * (100 - Number(comp.discount_pct)) / 100);
    }
    return unit * qty;
  }

  function eevySelectionTotalCents(items, comps) {
    if (!Array.isArray(comps) || comps.length === 0) return null;
    var total = 0;
    for (var i = 0; i < comps.length; i++) {
      var f = eevyComponentFinalCents(items, comps[i]);
      if (f == null) return null;
      total += f;
    }
    return total;
  }

  // Largest real component-level savings across campaign tiers/selections.
  // Returns { pct, amountCents } or null when no selection is cheaper than base.
  function eevyCampaignComponentSavings(base, campaign) {
    try {
      var items = (base && base.items) || {};
      var baseTiers = (base && base.tiers) || {};
      var campTiers = (campaign && campaign.tiers) || {};
      if (!items || !baseTiers || !campTiers) return null;
      var best = null;
      Object.keys(campTiers).forEach(function (tk) {
        var campTier = campTiers[tk];
        var baseTier = baseTiers[tk];
        if (!campTier || !baseTier) return;
        var campCbs = campTier.components_by_selection;
        var baseCbs = baseTier.components_by_selection || {};
        if (!campCbs || typeof campCbs !== 'object') return;
        var baseDisplay = Number(baseTier.display_price_cents) || 0;
        var baseSelKeys = Object.keys(baseCbs);
        Object.keys(campCbs).forEach(function (sk) {
          var campComps = campCbs[sk];
          var baseComps = baseCbs[sk] || (baseSelKeys.length ? baseCbs[baseSelKeys[0]] : null);
          if (!baseComps) return;
          var baseTotal = eevySelectionTotalCents(items, baseComps);
          var campTotal = eevySelectionTotalCents(items, campComps);
          if (baseTotal == null || campTotal == null) return;
          if (!(campTotal < baseTotal)) return;
          var amountCents = baseTotal - campTotal;
          var denom = baseDisplay > 0 ? baseDisplay : baseTotal;
          if (!(denom > 0)) return;
          var pct = Math.round(amountCents / denom * 100);
          if (!best || pct > best.pct || (pct === best.pct && amountCents > best.amountCents)) {
            best = { pct: pct, amountCents: amountCents };
          }
        });
      });
      return best;
    } catch (e) { return null; }
  }

  // Authored display copy from the newest campaign object shape. Prefer a
  // tier-level spart_message / badge when present; fall back to campaign.message.
  function eevyCampaignDisplayCopy(campaign) {
    var message = (campaign && campaign.message != null && String(campaign.message).trim())
      ? String(campaign.message).trim()
      : null;
    var badge = null;
    var spart = null;
    var tiers = (campaign && campaign.tiers) || {};
    Object.keys(tiers).forEach(function (tk) {
      var t = tiers[tk];
      if (!t || typeof t !== 'object') return;
      if (!badge && t.badge != null && String(t.badge).trim()) badge = String(t.badge).trim();
      if (!spart && t.spart_message != null && String(t.spart_message).trim()) {
        spart = String(t.spart_message).trim();
      }
    });
    return {
      message: message,
      badge: badge,
      spart_message: spart || message
    };
  }

  function collectEevyCampaigns(current) {
    try {
      var base = window.EevyBundleConfigBase;
      if (!base || !base.campaigns || typeof base.campaigns !== 'object') return [];
      var rt = window.Eevy && window.Eevy.bundles;
      var nowMs = (function () { try { return Date.now(); } catch (e) { return 0; } })();
      var handle = current && current.product_handle
        ? String(current.product_handle).toLowerCase()
        : '';
      var out = [];
      Object.keys(base.campaigns).forEach(function (key) {
        var campaign = base.campaigns[key];
        if (!campaign || typeof campaign !== 'object') return;
        // Need a code (or aliases via identifiers) to match the cookie/URL.
        if (!campaign.code && !(Array.isArray(campaign.utm_aliases) && campaign.utm_aliases.length)) return;
        var active = (rt && typeof rt.campaignActiveNow === 'function')
          ? rt.campaignActiveNow(campaign, nowMs)
          : eevyCampaignActiveFallback(campaign, nowMs);
        if (!active) return;

        // Admission is status/window + identity — NOT a display-price delta.
        // Component-level / message-only campaigns (pct=0) must still resolve
        // so promo-status + cart banner can show message/badge/spart.
        var pct = eevyCampaignDiscountPercent(rt, base, campaign);
        if (!(pct > 0)) {
          var compSave = eevyCampaignComponentSavings(base, campaign);
          if (compSave && compSave.pct > 0) pct = compSave.pct;
        }

        var aliases = (rt && typeof rt.campaignIdentifiers === 'function')
          ? rt.campaignIdentifiers(campaign)
          : eevyCampaignIdentifiersFallback(campaign);
        var copy = eevyCampaignDisplayCopy(campaign);
        out.push({
          active: true,
          code: campaign.code || (aliases[0] ? String(aliases[0]).toUpperCase() : null),
          aliases: aliases,
          templates: [],
          product_handles: handle ? [handle] : [],
          variant_ids: [],
          type: 'percentage',
          percentage: pct, // may be 0 — message/badge alone is valid
          // product-kind so the picker / promo order-discount composer
          // (which only stacks kind:'order' percentages on top of the offer
          // price) ignores it — preventing a double discount on the headline.
          kind: 'product',
          combines: { order: true, product: true },
          eevy_bundle: true,
          eevy_campaign_key: key,
          display_covered: true,
          message: copy.message,
          badge: copy.badge,
          spart_message: copy.spart_message
        });
      });
      return out;
    } catch (e) { return []; }
  }

  // ────────────────────────────────────────────────────────────────────────
  // Registered Eevy campaign recognition (influencer-fallback guard)
  //
  // isRegisteredEevyCampaignCode() answers: "is this code the canonical code
  // or a utm-alias of an ACTIVE campaign in window.EevyBundleConfigBase.campaigns?"
  // Kept even after message/component campaigns are ingested — a race where
  // the base config is present but collectEevyCampaigns has not yet run must
  // never fall through to the synthetic `<LETTERS>20` influencer paint over
  // a registered Eevy code (a9401f0 / TANDBERG20 class of bug).
  //
  // Fail-open: on missing/malformed base config it returns false, so unknown
  // codes and true influencer codes keep working unchanged. Prefers the
  // window.Eevy.bundles runtime helpers; falls back to local equivalents when
  // the runtime asset hasn't published yet but the base config has.
  function eevyBoundMsFallback(v) {
    if (v == null || v === '') return NaN;
    if (typeof v === 'number') { if (!isFinite(v)) return NaN; return v < 1e12 ? v * 1000 : v; }
    return Date.parse(v);
  }
  function eevyCampaignActiveFallback(campaign, atMs) {
    if (!campaign || campaign.status !== 'active') return false;
    var start = eevyBoundMsFallback(campaign.starts_at);
    var end = eevyBoundMsFallback(campaign.ends_at);
    if (!isNaN(start) && atMs < start) return false;
    if (!isNaN(end) && atMs > end) return false;
    return true;
  }
  function eevyCampaignIdentifiersFallback(campaign) {
    var ids = [];
    if (campaign && campaign.code) {
      var c = String(campaign.code).toLowerCase().trim();
      if (c) ids.push(c);
    }
    var aliases = campaign && campaign.utm_aliases;
    if (Array.isArray(aliases)) {
      aliases.forEach(function (a) {
        var s = String(a || '').toLowerCase().trim();
        if (s) ids.push(s);
      });
    }
    return ids;
  }
  function isRegisteredEevyCampaignCode(code) {
    return eevyCampaignNamed(code, true);
  }

  // Whether `code` is the code or a utm alias of a campaign in
  // window.EevyBundleConfigBase.campaigns; activeOnly skips ended or paused
  // ones.
  function eevyCampaignNamed(code, activeOnly) {
    try {
      var codeLc = String(code || '').toLowerCase().trim();
      if (!codeLc) return false;
      var base = window.EevyBundleConfigBase;
      if (!base || !base.campaigns || typeof base.campaigns !== 'object') return false;
      var rt = window.Eevy && window.Eevy.bundles;
      var atMs = (function () { try { return Date.now(); } catch (e) { return 0; } })();
      var keys = Object.keys(base.campaigns);
      for (var i = 0; i < keys.length; i++) {
        var campaign = base.campaigns[keys[i]];
        if (!campaign || typeof campaign !== 'object') continue;
        var active = !activeOnly || ((rt && typeof rt.campaignActiveNow === 'function')
          ? rt.campaignActiveNow(campaign, atMs)
          : eevyCampaignActiveFallback(campaign, atMs));
        if (!active) continue;
        var ids = (rt && typeof rt.campaignIdentifiers === 'function')
          ? rt.campaignIdentifiers(campaign)
          : eevyCampaignIdentifiersFallback(campaign);
        for (var j = 0; j < ids.length; j++) {
          if (String(ids[j] || '').toLowerCase().trim() === codeLc) return true;
        }
      }
      return false;
    } catch (e) { return false; }
  }

  function readStaticPromoConfig() {
    try {
      var cfg = window.DentisStaticPromoConfig;
      // The engine may run on a bundle PDP that has no DentisStaticPromoConfig
      // at all but DOES have an active Eevy campaign. Seed a minimal config from
      // the document context so Eevy ingestion still has `current` to scope by.
      if (!cfg) cfg = { current: {} };
      var out = { current: cfg.current || {} };

      if (cfg.code) {
        var value = Number(cfg.value);
        if (isFinite(value) && value > 0) {
          var type = (cfg.type === 'fixed' || cfg.type === 'fixed_amount') ? 'fixed' : 'percentage';
          out.single = { code: String(cfg.code).trim(), type: type, value: value };
        }
      }

      var campaigns = [];
      if (Array.isArray(cfg.campaigns) && cfg.campaigns.length > 0) {
        // Filter out campaigns with active:false so they're invisible to every
        // downstream code path (matchCampaign / findCampaignByCode /
        // collectCoreCampaigns / cookie-truth reconciliation). Missing or
        // active:true keeps the entry active — backward-compatible.
        campaigns = cfg.campaigns.filter(function (c) {
          return c && c.active !== false;
        });
      }

      // Append active Eevy bundle campaigns, normalized into native campaign
      // entries. They join the same campaigns[] the engine already resolves, so
      // matchCampaign / buildResolvedFromCampaign / filterByCartTruth handle
      // them with zero special-casing.
      var eevyCampaigns = collectEevyCampaigns(out.current);
      if (eevyCampaigns.length > 0) {
        // Don't shadow a DentisStaticPromoConfig campaign that already declares
        // the same code (the explicitly-configured one wins).
        var present = {};
        for (var ci = 0; ci < campaigns.length; ci++) {
          if (campaigns[ci] && campaigns[ci].code) {
            present[String(campaigns[ci].code).toLowerCase().trim()] = true;
          }
        }
        for (var ei = 0; ei < eevyCampaigns.length; ei++) {
          var ec = eevyCampaigns[ei];
          if (ec.code && present[String(ec.code).toLowerCase().trim()]) continue;
          campaigns.push(ec);
        }
      }

      if (campaigns.length > 0) out.campaigns = campaigns;

      if (!out.single && !out.campaigns) return null;
      return out;
    } catch (e) { return null; }
  }

  // A campaign matches a code on its canonical `code` OR any of its `aliases`
  // (case-insensitive). Aliases carry the Eevy `utm_aliases` so a shopper who
  // arrived on a utm-alias still resolves the same campaign the picker shows.
  function campaignCodeMatches(c, codeLc) {
    if (!c || !codeLc) return false;
    if (c.code && String(c.code).toLowerCase().trim() === codeLc) return true;
    if (Array.isArray(c.aliases)) {
      for (var ai = 0; ai < c.aliases.length; ai++) {
        if (String(c.aliases[ai] || '').toLowerCase().trim() === codeLc) return true;
      }
    }
    return false;
  }

  function matchCampaign(staticCfg, code) {
    if (!staticCfg || !Array.isArray(staticCfg.campaigns)) return null;
    var codeLc = (code || '').toLowerCase().trim();
    if (!codeLc) return null;
    var current = staticCfg.current || {};
    var currentTemplate = String(current.template_suffix || '').toLowerCase();
    var currentHandle = String(current.product_handle || '').toLowerCase();
    var currentVariantIds = Array.isArray(current.product_variant_ids) ? current.product_variant_ids : [];
    var currentVariantIdSet = {};
    for (var cvi = 0; cvi < currentVariantIds.length; cvi++) {
      currentVariantIdSet[String(currentVariantIds[cvi])] = true;
    }

    for (var i = 0; i < staticCfg.campaigns.length; i++) {
      var c = staticCfg.campaigns[i];
      if (!c || !c.code) continue;
      if (!campaignCodeMatches(c, codeLc)) continue;

      var templates = Array.isArray(c.templates) ? c.templates : [];
      var handles = Array.isArray(c.product_handles) ? c.product_handles : [];
      var variantIds = Array.isArray(c.variant_ids) ? c.variant_ids : [];

      // All three scopes are AND. Empty array = no scope restriction on that axis.
      var templateOk = true;
      var handleOk = true;
      var variantOk = true;
      if (templates.length > 0) {
        templateOk = false;
        for (var ti = 0; ti < templates.length; ti++) {
          if (String(templates[ti]).toLowerCase() === currentTemplate) { templateOk = true; break; }
        }
      }
      if (handles.length > 0) {
        handleOk = false;
        for (var hi = 0; hi < handles.length; hi++) {
          if (String(handles[hi]).toLowerCase() === currentHandle) { handleOk = true; break; }
        }
      }
      if (variantIds.length > 0) {
        // Campaign matches if the current PDP has at least one variant in the
        // campaign's variant_ids list. Per-variant discount restriction (so
        // unlisted variants of the same product stay full price) is handled
        // downstream in buildResolvedFromCampaign + getDiscountForVariant.
        variantOk = false;
        for (var vi = 0; vi < variantIds.length; vi++) {
          if (currentVariantIdSet[String(variantIds[vi])]) { variantOk = true; break; }
        }
      }
      if (templateOk && handleOk && variantOk) return c;
    }
    return null;
  }

  function findCampaignByCode(staticCfg, code) {
    if (!staticCfg || !Array.isArray(staticCfg.campaigns)) return null;
    var codeLc = (code || '').toLowerCase().trim();
    if (!codeLc) return null;
    for (var i = 0; i < staticCfg.campaigns.length; i++) {
      var c = staticCfg.campaigns[i];
      if (!c || !c.code) continue;
      if (campaignCodeMatches(c, codeLc)) return c;
    }
    return null;
  }

  function campaignMatchesCurrent(campaign, current) {
    if (!campaign) return false;
    var currentTemplate = String(current.template_suffix || '').toLowerCase();
    var currentHandle = String(current.product_handle || '').toLowerCase();
    var currentVariantIds = Array.isArray(current.product_variant_ids) ? current.product_variant_ids : [];
    var currentVariantIdSet = {};
    for (var cvi = 0; cvi < currentVariantIds.length; cvi++) {
      currentVariantIdSet[String(currentVariantIds[cvi])] = true;
    }
    var templates = Array.isArray(campaign.templates) ? campaign.templates : [];
    var handles = Array.isArray(campaign.product_handles) ? campaign.product_handles : [];
    var variantIds = Array.isArray(campaign.variant_ids) ? campaign.variant_ids : [];
    if (templates.length > 0) {
      var tok = false;
      for (var ti = 0; ti < templates.length; ti++) {
        if (String(templates[ti]).toLowerCase() === currentTemplate) { tok = true; break; }
      }
      if (!tok) return false;
    }
    if (handles.length > 0) {
      var hok = false;
      for (var hi = 0; hi < handles.length; hi++) {
        if (String(handles[hi]).toLowerCase() === currentHandle) { hok = true; break; }
      }
      if (!hok) return false;
    }
    if (variantIds.length > 0) {
      var vok = false;
      for (var vi = 0; vi < variantIds.length; vi++) {
        if (currentVariantIdSet[String(variantIds[vi])]) { vok = true; break; }
      }
      if (!vok) return false;
    }
    return true;
  }

  // Walk a primary campaign's `also_apply` list (recursively), collecting every
  // matching child campaign for the current PDP. Cycle-safe via visited set.
  // Returns array of child campaigns, in declaration order, that match the
  // current PDP scope. Children whose scope doesn't intersect with the current
  // PDP are skipped from the display layer but still applied at the cart layer
  // (see stackChildDiscountsOnCart).
  function collectChildCampaigns(staticCfg, primary) {
    var out = [];
    if (!primary || !Array.isArray(primary.also_apply) || primary.also_apply.length === 0) return out;
    var visited = {};
    visited[String(primary.code || '').toLowerCase().trim()] = true;
    var current = staticCfg.current || {};

    function walk(codeList) {
      for (var i = 0; i < codeList.length; i++) {
        var code = String(codeList[i] || '').trim();
        var key = code.toLowerCase();
        if (!code || visited[key]) continue;
        visited[key] = true;
        var child = findCampaignByCode(staticCfg, code);
        if (!child) continue;
        if (campaignMatchesCurrent(child, current)) out.push(child);
        if (Array.isArray(child.also_apply) && child.also_apply.length > 0) walk(child.also_apply);
      }
    }
    walk(primary.also_apply);
    return out;
  }

  // Returns the list of child codes (regardless of PDP scope) that should be
  // stacked on the cart by /discount/<code>?redirect=/cart.js. Used for the
  // checkout-side effect; display layer uses the scoped list above.
  function collectChildCodesForCart(staticCfg, primary) {
    var out = [];
    if (!primary || !Array.isArray(primary.also_apply) || primary.also_apply.length === 0) return out;
    var visited = {};
    visited[String(primary.code || '').toLowerCase().trim()] = true;

    function walk(codeList) {
      for (var i = 0; i < codeList.length; i++) {
        var code = String(codeList[i] || '').trim();
        var key = code.toLowerCase();
        if (!code || visited[key]) continue;
        visited[key] = true;
        out.push(code);
        var child = findCampaignByCode(staticCfg, code);
        if (child && Array.isArray(child.also_apply) && child.also_apply.length > 0) walk(child.also_apply);
      }
    }
    walk(primary.also_apply);
    return out;
  }

  function buildResolvedFromStaticConfig(code, cfg) {
    return {
      active: true,
      code: code,
      type: cfg.type,
      value: cfg.value,
      value_type: cfg.type === 'percentage' ? 'percentage' : 'fixed_amount',
      eligible: {},
      target_selection: 'all',
      target_type: 'line_item',
      raw_app: null,
      cart_token: null,
      source: 'static-config',
      // Storewide single is by convention an order-level discount that
      // combines with everything. Hardcoded — not exposed in theme settings.
      kind: 'order',
      combines: { order: true, product: true },
      core: false
    };
  }

  // Walk all configured campaigns and return the ones marked `core: true`
  // whose scope (templates / handles / variant_ids) intersects the current
  // PDP. These are always-on default-paint discounts (e.g. a bundle's
  // intrinsic deal price) that exist alongside whatever campaign cookie a
  // customer arrives with.
  //
  // The engine includes cores in EVERY resolve flow (cookie-matched
  // campaign, cookie-matched storewide single, or no-cookie-at-all). Per-
  // variant composition in price-override decides whether each core
  // survives the combinability matrix vs the cookie-matched discount.
  function collectCoreCampaigns(staticCfg) {
    if (!staticCfg || !Array.isArray(staticCfg.campaigns)) return [];
    var out = [];
    var current = staticCfg.current || {};
    for (var i = 0; i < staticCfg.campaigns.length; i++) {
      var c = staticCfg.campaigns[i];
      if (!c || c.core !== true) continue;
      if (campaignMatchesCurrent(c, current)) out.push(c);
    }
    return out;
  }

  // Build a self-contained sub-resolved for a single campaign. The "code"
  // param is the cookie / chain entry-point code; for the primary it equals
  // campaign.code, for children it's the child's own code.
  function buildSingleCampaignResolved(code, campaign, current) {
    var eligible = {};
    var campaignVariantIds = Array.isArray(campaign.variant_ids) ? campaign.variant_ids : [];
    if (current && current.product_id) {
      var bucket = { savings_total: 0, variants: {} };
      if (campaignVariantIds.length > 0) {
        bucket.restrict_to_variants = true;
        for (var bvi = 0; bvi < campaignVariantIds.length; bvi++) {
          bucket.variants[String(campaignVariantIds[bvi])] = 0;
        }
      }
      eligible[String(current.product_id)] = bucket;
    }

    var pct = Number(campaign.percentage || 0);
    var amt = Number(campaign.amount || 0);
    var type, value;
    if (pct > 0) {
      type = 'percentage';
      value = pct;
    } else if (amt > 0) {
      type = 'fixed';
      value = amt;
    } else {
      type = 'percentage';
      value = 0;
    }

    return {
      active: true,
      code: code,
      type: type,
      value: value,
      value_type: type === 'percentage' ? 'percentage' : 'fixed_amount',
      eligible: eligible,
      target_selection: 'entitled',
      target_type: 'line_item',
      source: campaign.eevy_bundle === true ? 'static-config:eevy-campaign' : 'static-config:campaign',
      campaign_code: campaign.code || null,
      // Eevy bundle markers (see collectEevyCampaigns). Carried onto the sub so
      // price-override skips painting and promo-status derives the offer % —
      // benign/undefined for non-Eevy campaigns.
      eevy_bundle: campaign.eevy_bundle === true,
      eevy_campaign_key: campaign.eevy_campaign_key || null,
      display_covered: campaign.display_covered === true,
      // Authored campaign copy (newest Eevy shape). promo-status prefers these
      // when no numeric savings is derivable — message-only campaigns stay useful.
      message: campaign.message != null ? campaign.message : null,
      badge: campaign.badge != null ? campaign.badge : null,
      spart_message: campaign.spart_message != null ? campaign.spart_message : null,
      // Combinability + kind metadata used by price-override's per-variant
      // composer. Campaigns are product-kind by default, but honor an
      // explicit `kind: "order"` in the JSON (e.g. VELKOMMEN15). Order-kind
      // subs MUST be classified as such so the composer sequences them AFTER
      // product discounts — Shopify allocates order discounts on the
      // post-line-discount subtotal, so a 15% order code applies to the price
      // that remains after a fixed product deal (DUO-PAKKEPRIS), not to the
      // compare-at/base price in isolation.
      kind: Combinability.kindOf(campaign),
      combines: Combinability.normalize(campaign),
      core: campaign.core === true
    };
  }

  // Build the full resolved payload for a primary campaign and any child
  // campaigns chained via `also_apply`. The returned object exposes:
  //   - root: top-level fields mirror the PRIMARY for backward compat with
  //     anything reading `resolved.type` / `resolved.value` / `resolved.code`
  //   - resolved.discounts: array of every matching sub-resolved (primary
  //     first), so price-override can iterate per-variant and pick the
  //     first one that matches.
  // Build a resolved payload that contains ONLY cores. Used when the
  // customer has no campaign cookie (and no storewide single match) but the
  // current PDP has core discounts to paint.
  function buildCoresOnlyResolved(coreCampaigns, current) {
    if (!coreCampaigns || coreCampaigns.length === 0) {
      return { active: false, code: null, source: 'cores-only:empty' };
    }
    var subs = coreCampaigns.map(function (c) {
      return buildSingleCampaignResolved(c.code, c, current);
    });
    var first = subs[0];
    return {
      active: true,
      code: first.code,
      type: first.type,
      value: first.value,
      value_type: first.value_type,
      eligible: first.eligible,
      target_selection: first.target_selection,
      target_type: first.target_type,
      source: 'cores-only',
      discounts: subs
    };
  }

  // Build a resolved payload for an influencer-pattern cookie code. The
  // synthesized sub is order-kind 20% off, fully combinable. Cores (if any)
  // are prepended so the per-variant matrix composer in price-override
  // stacks the influencer % on top of the core deal naturally (cross-kind
  // product+order, both combines all-true).
  function buildInfluencerResolved(code, coreCampaigns, current) {
    var influencerSub = {
      active: true,
      code: code,
      type: 'percentage',
      value: INFLUENCER_DEFAULT_PERCENTAGE,
      value_type: 'percentage',
      eligible: {},
      target_selection: 'all',
      target_type: 'line_item',
      source: 'influencer-pattern',
      kind: 'order',
      combines: { order: true, product: true },
      core: false
    };
    var subs = [];
    if (Array.isArray(coreCampaigns)) {
      for (var i = 0; i < coreCampaigns.length; i++) {
        subs.push(buildSingleCampaignResolved(coreCampaigns[i].code, coreCampaigns[i], current));
      }
    }
    subs.push(influencerSub);
    return {
      active: true,
      code: code,
      type: influencerSub.type,
      value: influencerSub.value,
      value_type: influencerSub.value_type,
      eligible: {},
      target_selection: 'all',
      target_type: 'line_item',
      source: subs.length > 1 ? 'influencer+cores' : 'influencer',
      discounts: subs
    };
  }

  function buildResolvedFromCampaign(code, primaryCampaign, current, childCampaigns, coreCampaigns) {
    var primary = buildSingleCampaignResolved(code, primaryCampaign, current);
    // Cores first, then primary, then children. Order doesn't affect the
    // combinability matrix (which is set-based) but makes per-variant traces
    // easier to read: "core was here, then the cookie code, then chained".
    var discounts = [];
    if (Array.isArray(coreCampaigns)) {
      for (var coi = 0; coi < coreCampaigns.length; coi++) {
        discounts.push(buildSingleCampaignResolved(coreCampaigns[coi].code, coreCampaigns[coi], current));
      }
    }
    discounts.push(primary);
    if (Array.isArray(childCampaigns)) {
      for (var ci = 0; ci < childCampaigns.length; ci++) {
        discounts.push(buildSingleCampaignResolved(childCampaigns[ci].code, childCampaigns[ci], current));
      }
    }
    return {
      active: true,
      code: primary.code,
      type: primary.type,
      value: primary.value,
      value_type: primary.value_type,
      eligible: primary.eligible,
      target_selection: primary.target_selection,
      target_type: primary.target_type,
      raw_app: null,
      cart_token: null,
      source: primary.eevy_bundle ? 'static-config:eevy-campaign' : 'static-config:campaign',
      campaign_code: primary.campaign_code,
      eevy_bundle: primary.eevy_bundle === true,
      eevy_campaign_key: primary.eevy_campaign_key || null,
      display_covered: primary.display_covered === true,
      message: primary.message || null,
      badge: primary.badge || null,
      spart_message: primary.spart_message || null,
      discounts: discounts
    };
  }

  // POST /discount/<code>?redirect=/cart.js for every code passed in. Returns
  // a Promise.all so the caller can deterministically chain post-apply work
  // (re-fetching /cart.js, dispatching the final resolved state, etc).
  //
  // The CALLER decides which codes to fire (typically by filtering out codes
  // that are already on the cart via indexCartDiscountCodes); this function
  // does no de-duplication. It skips Eevy campaign codes: those reach the
  // cart only through the runtime's reconcile
  // (dentis-bundle-config.liquid), which also removes the campaign they
  // replace; a /discount/ here would re-add an earlier campaign's code and
  // cookie. Both engine writers (this and applyDiscountToCart) skip them.
  function stackDiscountsOnCart(codes) {
    if (!Array.isArray(codes) || codes.length === 0) return Promise.resolve();
    var pending = [];
    for (var i = 0; i < codes.length; i++) {
      var code = String(codes[i] || '').trim();
      if (!code || eevyCampaignNamed(code, false)) continue;
      pending.push((function (theCode) {
        try {
          return fetch('/discount/' + encodeURIComponent(theCode) + '?redirect=/cart.js', {
            credentials: 'same-origin',
            cache: 'no-store'
          })
            .then(function () { DBG.log('discount applied on cart', theCode); })
            .catch(function (err) { DBG.warn('discount apply failed', theCode, err); });
        } catch (e) {
          DBG.warn('discount fetch threw', theCode, e);
          return Promise.resolve();
        }
      })(code));
    }
    return pending.length > 0 ? Promise.all(pending) : Promise.resolve();
  }

  // Attach core-discount codes to the cart session (firing /discount/<code>
  // for each one not already present in the cart). After cores are attached,
  // re-fires `restoreCookieCode` last so the discount cookie ends as the
  // primary code the engine expects on the next page load — otherwise the
  // cookie would be whichever core code was POSTed last, and the engine
  // would route the next resolve() through the wrong flow (campaign vs
  // influencer vs single). An Eevy primary is not re-fired (stackDiscountsOnCart
  // skips it; its cookie comes from dentis-bundle-config.liquid), so after a
  // missing core is attached the cookie holds that core.
  //
  // Why this exists: cores (`core:true` in the JSON) are paint-as-always-on
  // by intent, but if the Shopify-side discount is CODE-based (not
  // automatic), Shopify only applies it when the code is on the cart
  // session. The original "core" assumption was that they'd be automatic
  // discounts that always apply; this helper closes the gap for code-based
  // cores.
  function attachCoresAndRestoreCookie(coreCodes, restoreCookieCode, cart) {
    var idx = indexCartDiscountCodes(cart);
    var missing = (coreCodes || []).filter(function (c) {
      return c && !idx.present[String(c).toLowerCase().trim()];
    });
    if (missing.length === 0) {
      DBG.t('CORES-ATTACH', 'all cores already on cart, no /discount/ POSTs needed', {
        cores: coreCodes,
        cart_present: Object.keys(idx.present)
      });
      return Promise.resolve();
    }
    DBG.t('CORES-ATTACH', 'firing /discount/ for missing core codes', {
      missing: missing,
      already_present: Object.keys(idx.present),
      restore_cookie_to: restoreCookieCode || null
    });
    return stackDiscountsOnCart(missing).then(function () {
      if (restoreCookieCode) {
        DBG.t('CORES-ATTACH', 'restoring cookie to primary by re-firing it last', { code: restoreCookieCode });
        return stackDiscountsOnCart([restoreCookieCode]);
      }
    });
  }

  // Build a lowercase-keyed index of cart.discount_codes for O(1) lookup of
  // "is this code on the cart at all" (present) and "is Shopify currently
  // honoring it" (applicable). Empty/missing cart returns an empty index.
  function indexCartDiscountCodes(cart) {
    var idx = { present: {}, applicable: {} };
    if (!cart || !Array.isArray(cart.discount_codes)) return idx;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var dc = cart.discount_codes[i] || {};
      if (!dc.code) continue;
      var key = String(dc.code).toLowerCase().trim();
      idx.present[key] = true;
      if (dc.applicable) idx.applicable[key] = true;
    }
    return idx;
  }

  function cartIsEmpty(cart) {
    if (!cart) return true;
    var n = (cart.items && cart.items.length) || cart.item_count || 0;
    return n === 0;
  }

  // Given a synthesized resolved (built from JSON config) and a /cart.js
  // snapshot, return the resolved state we should actually dispatch.
  //
  // Rules:
  //   - Empty cart -> trust synthesized. Shopify marks EVERY discount_code
  //     applicable:false when there are no items in the cart, so applicability
  //     is uninformative here. The PDP is the customer's first impression of
  //     the offer — paint the synthesized prices and let real cart values
  //     render via Liquid when items get added.
  //   - Non-empty cart -> for each synthesized sub-discount, check whether its
  //     code is in cart.discount_codes with applicable:true. Drop the
  //     non-applicable ones. This catches typos, expired codes, customer
  //     ineligibility, and combinability mismatches without giving a false
  //     positive on empty-cart sessions.
  //
  // Returns one of:
  //   - the synthesized resolved unchanged (no codes dropped)
  //   - a corrected resolved with only the surviving sub-discounts
  //   - { active: false, source: 'cart-truth:all-rejected', ... }
  function filterByCartTruth(synthesized, cart) {
    if (!synthesized || !synthesized.active) return synthesized;
    if (cartIsEmpty(cart)) {
      DBG.t('FILTER', 'cart empty -> trusting synthesized (applicable flag is meaningless on empty cart)');
      return synthesized;
    }

    var cartIndex = indexCartDiscountCodes(cart);
    var subs = Array.isArray(synthesized.discounts) && synthesized.discounts.length > 0
      ? synthesized.discounts
      : [synthesized];

    var kept = [];
    var dropped = [];
    var decisions = [];
    for (var si = 0; si < subs.length; si++) {
      var sub = subs[si];
      var key = String(sub.code || synthesized.code || '').toLowerCase().trim();
      // Cores are trusted from JSON config. They typically map to is_automatic
      // discounts in Shopify which DON'T appear in cart.discount_codes, so
      // checking applicability here would always reject them. Skip the cart
      // check for cores and trust the static config.
      if (sub.core === true) {
        kept.push(sub);
        decisions.push({ code: sub.code, decision: 'kept-core', cart_present: !!cartIndex.present[key], note: 'core: skip cart check' });
        continue;
      }
      // Eevy bundle campaigns are config-driven (same trust rationale as cores):
      // the displayed offer comes from the Eevy effective config, and the
      // gid-managed Shopify discount may not appear in cart.discount_codes on an
      // empty/non-entitled PDP cart. Trust the static config, skip the cart check.
      if (sub.eevy_bundle === true) {
        kept.push(sub);
        decisions.push({ code: sub.code, decision: 'kept-eevy', cart_present: !!cartIndex.present[key], note: 'eevy_bundle: skip cart check' });
        continue;
      }
      if (cartIndex.applicable[key]) {
        kept.push(sub);
        decisions.push({ code: sub.code, decision: 'kept', cart_present: true, cart_applicable: true });
      } else {
        dropped.push(sub.code);
        decisions.push({
          code: sub.code,
          decision: 'dropped',
          cart_present: !!cartIndex.present[key],
          cart_applicable: false
        });
      }
    }
    DBG.t('FILTER', 'per-code decisions', { decisions: decisions, kept: kept.length, dropped: dropped.length });

    if (dropped.length === 0) {
      return synthesized;
    }

    DBG.tw('FILTER', 'Shopify rejected codes', { rejected: dropped });

    if (kept.length === 0) {
      return {
        active: false,
        code: synthesized.code,
        source: 'cart-truth:all-rejected',
        rejected_codes: dropped
      };
    }

    // When the synthesized had a "primary" cookie code that got rejected but
    // cores survived, the top-level code should reflect the surviving primary
    // (first non-core kept entry), falling back to the first kept overall.
    var primary = null;
    for (var pk = 0; pk < kept.length; pk++) {
      if (!kept[pk].core) { primary = kept[pk]; break; }
    }
    if (!primary) primary = kept[0];
    var corrected = {
      active: true,
      code: primary.code,
      type: primary.type,
      value: primary.value,
      value_type: primary.value_type,
      eligible: primary.eligible,
      target_selection: primary.target_selection,
      target_type: primary.target_type,
      source: 'cart-truth:filtered',
      rejected_codes: dropped,
      // Preserve Eevy markers + authored copy when the surviving primary is an
      // ingested campaign (message/badge still need to render after filtering).
      eevy_bundle: primary.eevy_bundle === true,
      eevy_campaign_key: primary.eevy_campaign_key || null,
      display_covered: primary.display_covered === true,
      message: primary.message || null,
      badge: primary.badge || null,
      spart_message: primary.spart_message || null
    };
    if (kept.length > 1) corrected.discounts = kept;
    return corrected;
  }

  // Shallow equality on the fields that drive paint behavior. Used to suppress
  // redundant re-dispatches that would otherwise cause visible flicker when
  // the deterministic pipeline arrives at the same state across phase 1 and
  // phase 2 reads.
  function resolvedShallowEquals(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (!!a.active !== !!b.active) return false;
    if (!a.active) return true;
    if (String(a.code || '') !== String(b.code || '')) return false;
    if (a.type !== b.type) return false;
    if (Number(a.value || 0) !== Number(b.value || 0)) return false;
    var aLen = Array.isArray(a.discounts) ? a.discounts.length : 1;
    var bLen = Array.isArray(b.discounts) ? b.discounts.length : 1;
    if (aLen !== bLen) return false;
    return true;
  }

  function nowMs() {
    return new Date().getTime();
  }

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function readUrlParam() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      for (var i = 0; i < URL_PARAMS.length; i++) {
        var v = params.get(URL_PARAMS[i]);
        if (v) return v;
      }
    } catch (e) { /* no-op */ }
    return null;
  }

  function readCachedResult(code) {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.timestamp || (nowMs() - parsed.timestamp) > STORAGE_TTL_MS) return null;
      if ((parsed.code || '').toLowerCase() !== (code || '').toLowerCase()) return null;
      return parsed.resolved || null;
    } catch (e) {
      return null;
    }
  }

  function writeCachedResult(code, resolved) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        code: code,
        timestamp: nowMs(),
        resolved: resolved
      }));
    } catch (e) { /* quota or disabled - fine */ }
  }

  function clearCache() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* no-op */ }
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }, opts || {})).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  // /cart/update.js {discount} replaces the cart's code list, so the codes
  // already on the cart are sent along. Eevy campaign codes are never written
  // here (see stackDiscountsOnCart).
  function applyDiscountToCart(code) {
    if (eevyCampaignNamed(code, false)) return Promise.resolve(null);
    return getCart().then(function (cart) {
      var codes = ((cart && cart.discount_codes) || []).map(function (d) { return String(d && d.code || '').trim(); })
        .filter(function (c) { return !!c; });
      var codeLc = String(code).toLowerCase().trim();
      if (codes.some(function (c) { return c.toLowerCase() === codeLc; })) return cart;
      return fetchJSON('/cart/update.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discount: codes.concat([code]).join(',') })
      });
    });
  }

  function getCart() {
    return fetchJSON('/cart.js');
  }

  function appMatchesCode(app, code) {
    var lc = (code || '').toLowerCase();
    return (app.title || '').toLowerCase() === lc
      || (app.code || '').toLowerCase() === lc;
  }

  function buildEligibilityFromCart(cart, code) {
    var eligible = {};
    var cartLevelApps = (cart && cart.cart_level_discount_applications) || [];
    var legacyApps = (cart && cart.discount_applications) || [];
    var apps = cartLevelApps.concat(legacyApps);

    var matchingApp = null;
    for (var i = 0; i < apps.length; i++) {
      if (appMatchesCode(apps[i] || {}, code)) {
        matchingApp = apps[i];
        break;
      }
    }
    if (!matchingApp && apps.length === 1) {
      matchingApp = apps[0];
    }

    if (!matchingApp) {
      var scanItems = (cart && cart.items) || [];
      for (var si = 0; si < scanItems.length; si++) {
        var scanAllocs = (scanItems[si] && (scanItems[si].line_level_discount_allocations || scanItems[si].discount_allocations)) || [];
        for (var sa = 0; sa < scanAllocs.length; sa++) {
          var lineApp = (scanAllocs[sa] && scanAllocs[sa].discount_application) || null;
          if (lineApp && appMatchesCode(lineApp, code)) {
            matchingApp = lineApp;
            apps = apps.concat([lineApp]);
            break;
          }
        }
        if (matchingApp) break;
      }
    }

    var items = (cart && cart.items) || [];
    for (var j = 0; j < items.length; j++) {
      var item = items[j] || {};
      var saved = 0;

      // Modern shape: line_level_discount_allocations[*].discount_application + amount
      var lineAllocs = item.line_level_discount_allocations || item.discount_allocations || [];
      var savedFromLineAllocs = 0;
      for (var k = 0; k < lineAllocs.length; k++) {
        var alloc = lineAllocs[k] || {};
        var allocApp = alloc.discount_application || {};
        var matches = appMatchesCode(allocApp, code) || (apps.length === 1);
        if (matches) savedFromLineAllocs += Number(alloc.amount || 0);
      }
      saved += savedFromLineAllocs;

      // item.discounts[] mirrors line_level_discount_allocations[] for entitled-target
      // codes; only consult it as a fallback when line allocations contributed nothing
      // for this item, to avoid double-counting the same per-code amount.
      if (savedFromLineAllocs === 0) {
        var lineDiscounts = item.discounts || [];
        for (var d = 0; d < lineDiscounts.length; d++) {
          var disc = lineDiscounts[d] || {};
          if ((disc.title || '').toLowerCase() === (code || '').toLowerCase()) {
            saved += Number(disc.amount || 0);
          }
        }
      }

      // Total-discount fallback: when only one cart-level discount is in play
      // and matches our code, item.total_discount captures the across-allocated
      // amount even when neither line_level_discount_allocations nor discounts
      // resolves into a per-code entry.
      if (saved === 0 && matchingApp && appMatchesCode(matchingApp, code)) {
        var totalDisc = Number(item.total_discount || 0);
        if (totalDisc > 0) saved = totalDisc;
      }

      if (saved > 0) {
        var pid = String(item.product_id);
        var vid = String(item.variant_id);
        if (!eligible[pid]) eligible[pid] = { savings_total: 0, variants: {} };
        eligible[pid].savings_total += saved;
        eligible[pid].variants[vid] = (eligible[pid].variants[vid] || 0) + (saved / Math.max(1, item.quantity));
      }
    }
    return { eligible: eligible, app: matchingApp, allApps: apps };
  }

  function appType(app) {
    if (!app) return 'unknown';
    var v = app.value_type || app.type || '';
    var typeStr = (app.type || '').toLowerCase();
    if (typeStr === 'shipping_discount' || typeStr === 'shipping') return 'shipping';
    if (typeStr === 'automatic') {
      // Automatic discounts may also be percentage/fixed; rely on value_type
    }
    if (v === 'percentage') return 'percentage';
    if (v === 'fixed_amount') return 'fixed';
    if (typeStr === 'discount_code' && (app.target_type === 'line_item' || !app.target_type)) {
      return v === 'percentage' ? 'percentage' : 'fixed';
    }
    return typeStr || 'unknown';
  }

  function buildResolved(code, cart) {
    if (!cart) {
      return { active: false, code: code || null };
    }
    var built = buildEligibilityFromCart(cart, code);
    var app = built.app;
    if (!app) {
      return { active: false, code: code || null };
    }
    DBG.log('matched app', app, 'all apps:', built.allApps);
    var type = appType(app);
    if (type === 'shipping') {
      return {
        active: true,
        code: code || app.title || app.code || null,
        type: 'shipping',
        value: Number(app.value || 0),
        value_type: app.value_type || null,
        eligible: {},
        target_selection: app.target_selection || null,
        raw_app: app,
        cart_token: cart.token || null
      };
    }
    var resolvedValue = Number(app.value || 0);

    // Shopify Function-based automatic discounts (DiscountAutomaticApp) often
    // expose value="" / value_type=null in the storefront cart payload, so we
    // can't read the percentage off the app object directly. When that happens
    // but the cart has at least one line allocation contributed by this code,
    // derive the percentage from the actual savings amount: pct = (savings /
    // quantity) / unit_price * 100. This lets other variants of the same
    // product (which getDiscountForVariant in price-override.js falls through
    // to the %-formula for) display the correct discounted price too.
    if (type === 'percentage' && (!isFinite(resolvedValue) || resolvedValue <= 0)) {
      var items = (cart && cart.items) || [];
      for (var ix = 0; ix < items.length; ix++) {
        var it = items[ix] || {};
        var unitPrice = Number(it.price || 0);
        if (unitPrice <= 0) continue;
        var qty = Math.max(1, Number(it.quantity || 1));
        var allocs = it.line_level_discount_allocations || it.discount_allocations || [];
        var totalLineSaved = 0;
        for (var ja = 0; ja < allocs.length; ja++) {
          var aApp = (allocs[ja] && allocs[ja].discount_application) || {};
          if (appMatchesCode(aApp, code) || built.allApps.length === 1) {
            totalLineSaved += Number(allocs[ja].amount || 0);
          }
        }
        if (totalLineSaved > 0) {
          var pct = (totalLineSaved / qty) / unitPrice * 100;
          // Snap to typical merchant-set values (5/10/15/20/25/30/...): if the
          // derived value is within 0.5 of a round number, round to it.
          var rounded = Math.round(pct);
          if (Math.abs(pct - rounded) < 0.5) pct = rounded;
          resolvedValue = pct;
          DBG.log('derived percentage from line allocation', { pct: pct, totalLineSaved: totalLineSaved, qty: qty, unitPrice: unitPrice });
          break;
        }
      }
    }

    return {
      active: true,
      code: code || app.title || app.code || null,
      type: type,
      value: resolvedValue,
      value_type: app.value_type || null,
      total_allocated: Number(app.total_allocated_amount || 0),
      eligible: built.eligible,
      target_selection: app.target_selection || null,
      target_type: app.target_type || null,
      raw_app: app,
      cart_token: cart.token || null
    };
  }

  var LAST_DISPATCHED = null;
  var DISPATCH_SEQ = 0;

  // Distill a resolved object to a compact, log-friendly summary. The full
  // resolved can be huge (eligibility maps, raw_app, cart_token, etc); the
  // summary surfaces only the fields that drive paint behavior + a diff hint
  // (codes[] flattened to a comma-list, per-discount value).
  function summarizeResolved(r) {
    if (!r) return { null: true };
    if (!r.active) {
      return { active: false, code: r.code || null, source: r.source || null, rejected: r.rejected_codes || null };
    }
    var subs = Array.isArray(r.discounts) && r.discounts.length > 0 ? r.discounts : [r];
    var codes = subs.map(function (s) {
      var bucket = s.eligible && Object.keys(s.eligible)[0];
      var variants = bucket && s.eligible[bucket] && s.eligible[bucket].variants
        ? Object.keys(s.eligible[bucket].variants).length
        : 0;
      return s.code + '(' + s.type + ':' + s.value + ',variants=' + variants + ')';
    });
    return {
      active: true,
      code: r.code,
      type: r.type,
      value: r.value,
      source: r.source || null,
      discount_count: subs.length,
      discounts: codes
    };
  }

  function dispatchResolved(resolved) {
    DISPATCH_SEQ++;
    var changed = LAST_DISPATCHED
      ? {
          code_changed: String((LAST_DISPATCHED || {}).code || '') !== String((resolved || {}).code || ''),
          active_changed: !!(LAST_DISPATCHED || {}).active !== !!(resolved || {}).active,
          type_changed: (LAST_DISPATCHED || {}).type !== (resolved || {}).type,
          value_changed: Number((LAST_DISPATCHED || {}).value || 0) !== Number((resolved || {}).value || 0)
        }
      : { first_dispatch: true };
    DBG.t('DISPATCH', 'dispatch #' + DISPATCH_SEQ, { resolved: summarizeResolved(resolved), changed: changed });
    LAST_DISPATCHED = resolved;
    window.DentisActiveDiscount = resolved;
    try {
      var ev = new CustomEvent(EVENT_NAME, { detail: resolved });
      document.dispatchEvent(ev);
    } catch (e) {
      DBG.tw('DISPATCH', 'CustomEvent constructor failed, using legacy fallback', { error: String(e) });
      var fallback = document.createEvent('CustomEvent');
      fallback.initCustomEvent(EVENT_NAME, false, false, resolved);
      document.dispatchEvent(fallback);
    }
  }

  // Dispatch suppressed when shallowly equal to the previous dispatch — keeps
  // the deterministic pipeline (read cart -> apply missing -> read cart ->
  // dispatch) from firing a redundant event that would force price-override
  // to restoreAll() + re-paint for the same final state. Eliminates the
  // visible-flicker class of bugs where two dispatches arrive at identical
  // resolved states across an async boundary.
  function dispatchResolvedOnce(resolved) {
    if (resolvedShallowEquals(LAST_DISPATCHED, resolved)) {
      DBG.t('DISPATCH-X', 'suppressed (identical to last)', { resolved: summarizeResolved(resolved) });
      return;
    }
    dispatchResolved(resolved);
  }

  // Serializes concurrent resolve() calls. Both the DOMContentLoaded handler
  // and the autoapply snippet's nudgeEngine -> refresh path can fire resolve()
  // around the same time; running both flows in parallel would double the
  // /discount/ POSTs and risk two competing dispatches.
  var RESOLVE_IN_FLIGHT = null;

  // The deterministic campaign pipeline, extracted so BOTH the cookie/url code
  // match AND the cart-truth discovery path resolve a matched campaign through
  // the identical machinery (synthesize -> attach missing codes -> re-read cart
  // -> filterByCartTruth -> dispatchResolvedOnce). No optimistic paint; flicker
  // eliminated; dedupe suppresses redundant re-dispatch on re-resolve.
  function runCampaignFlow(code, matchedCampaign, staticCfg, coreCampaigns) {
    var childCampaigns = collectChildCampaigns(staticCfg, matchedCampaign);
    // Pass cores into the synthesizer — they end up in resolved.discounts
    // first, then primary, then chained children. Filter out the primary
    // and chain from cores to avoid duplicates if the cookie code itself
    // is a core (rare but possible).
    var coresForCampaign = coreCampaigns.filter(function (c) {
      if (c.code === matchedCampaign.code) return false;
      for (var cci = 0; cci < childCampaigns.length; cci++) {
        if (c.code === childCampaigns[cci].code) return false;
      }
      return true;
    });
    var synthesized = buildResolvedFromCampaign(code, matchedCampaign, staticCfg.current, childCampaigns, coresForCampaign);
    var childCodesForCart = collectChildCodesForCart(staticCfg, matchedCampaign) || [];
    // Cores must also be attached as discount codes so Shopify checkout
    // actually applies them — `core:true` is a paint-as-always-on flag,
    // but if the underlying Shopify discount is code-based (not
    // automatic) it only applies when its code is on the cart session.
    var coreCodesForCart = coresForCampaign.map(function (c) { return c.code; });
    // Primary last so a non-Eevy /discount/<primary> leaves the cookie on it.
    var allExpectedCodes = coreCodesForCart.concat(childCodesForCart).concat([code]);

    DBG.t('CAMPAIGN', 'matched primary + chain + cores', {
      primary: matchedCampaign.code,
      primary_type: matchedCampaign.amount ? 'fixed:' + matchedCampaign.amount : 'percentage:' + matchedCampaign.percentage,
      primary_eevy: matchedCampaign.eevy_bundle === true,
      primary_variants: matchedCampaign.variant_ids || [],
      primary_combines: Combinability.normalize(matchedCampaign),
      child_count_paint: childCampaigns.length,
      child_codes_paint: childCampaigns.map(function (c) { return c.code; }),
      child_codes_cart: childCodesForCart,
      core_count: coresForCampaign.length,
      core_codes: coresForCampaign.map(function (c) { return c.code; }),
      synthesized: summarizeResolved(synthesized),
      all_expected_codes: allExpectedCodes
    });

    RESOLVE_IN_FLIGHT = getCart()
      .catch(function (err) { DBG.tw('P1-CART', '/cart.js failed', { error: String(err) }); return null; })
      .then(function (cart1) {
        DBG.t('P1-CART', 'snapshot', {
          item_count: cart1 ? (cart1.item_count || (cart1.items && cart1.items.length) || 0) : null,
          discount_codes: cart1 && cart1.discount_codes || null,
          token: cart1 && cart1.token || null
        });

        var idx1 = indexCartDiscountCodes(cart1);
        var missingCodes = allExpectedCodes.filter(function (c) {
          return !idx1.present[String(c).toLowerCase().trim()];
        });
        DBG.t('P2-DIFF', 'missing codes', {
          all_expected: allExpectedCodes,
          already_present: Object.keys(idx1.present),
          already_applicable: Object.keys(idx1.applicable),
          missing: missingCodes
        });

        if (missingCodes.length === 0) {
          DBG.t('P5-FAST', 'all codes on cart already; fast-path dispatch');
          var fast = filterByCartTruth(synthesized, cart1);
          DBG.t('FILTER', 'fast-path result', { input: summarizeResolved(synthesized), output: summarizeResolved(fast) });
          dispatchResolvedOnce(fast);
          return;
        }

        DBG.t('P3-FIRE', 'firing /discount/ POSTs', { codes: missingCodes });
        return stackDiscountsOnCart(missingCodes)
          .then(function () {
            DBG.t('P4-CART', 'phase 4 re-read /cart.js');
            return getCart().catch(function (err) {
              DBG.tw('P4-CART', '/cart.js failed, falling back to cart1', { error: String(err) });
              return cart1;
            });
          })
          .then(function (cart2) {
            DBG.t('P4-CART', 'snapshot after POSTs', {
              item_count: cart2 ? (cart2.item_count || (cart2.items && cart2.items.length) || 0) : null,
              discount_codes: cart2 && cart2.discount_codes || null
            });
            var final = filterByCartTruth(synthesized, cart2 || cart1);
            DBG.t('FILTER', 'final result', { input: summarizeResolved(synthesized), output: summarizeResolved(final) });
            dispatchResolvedOnce(final);
          });
      })
      .catch(function (err) {
        DBG.te('CAMPAIGN', 'flow failed, dispatching synthesized as last-resort', { error: String(err) });
        dispatchResolvedOnce(synthesized);
      })
      .then(function () { RESOLVE_IN_FLIGHT = null; DBG.t('RESOLVE-END', 'campaign flow finalized'); }, function () { RESOLVE_IN_FLIGHT = null; });
    return RESOLVE_IN_FLIGHT;
  }

  // Cart-truth discovery for Eevy bundle campaigns. The native engine only
  // picks a code from cookie/url/explicit; an Eevy code can instead be applied
  // to the cart SESSION (no JS cookie on a plain PDP nav). This reads /cart.js,
  // extracts the codes Shopify reports as applied, and returns the first that
  // matches an ingested Eevy campaign for the current PDP scope — so the
  // campaign resolves natively even without a cookie. Returns a Promise of the
  // matched code (or null). Only consults ingested Eevy campaigns; an unknown
  // cart code is left to the existing fallbacks.
  function discoverEevyCampaignCodeFromCart(staticCfg) {
    if (!staticCfg || !Array.isArray(staticCfg.campaigns)) return Promise.resolve(null);
    var hasEevy = staticCfg.campaigns.some(function (c) { return c && c.eevy_bundle === true; });
    if (!hasEevy) return Promise.resolve(null);
    return getCart()
      .catch(function () { return null; })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.discount_codes)) return null;
        for (var i = 0; i < cart.discount_codes.length; i++) {
          var dc = cart.discount_codes[i] || {};
          if (!dc.code || dc.applicable === false) continue;
          var matched = matchCampaign(staticCfg, dc.code);
          if (matched && matched.eevy_bundle === true) {
            DBG.t('EEVY-CART', 'discovered Eevy campaign code from cart truth', { code: dc.code, campaign: matched.eevy_campaign_key });
            return dc.code;
          }
        }
        return null;
      });
  }

  function readFreshEevyCampaignDecision() {
    try {
      var rt = window.Eevy && window.Eevy.bundles;
      var base = window.EevyBundleConfigBase;
      if (!rt || !base || typeof rt.readCampaignContext !== 'function'
        || typeof rt.resolveEffectiveBundleConfig !== 'function') return null;
      // Same context the bundle config snippet resolves with: this page's own
      // link outranks the resolved code and the cookie.
      var context = typeof window.DentisEevyCampaignContext === 'function'
        ? window.DentisEevyCampaignContext(rt)
        : rt.readCampaignContext();
      var signals = context && context.signals || {};
      var offerSignal = String(signals.eevyOffer || '').trim();
      var discountSignal = String(signals.discount || '').trim();
      var utmSignal = String(signals.utmCampaign || '').trim();
      // A ?discount= naming no Eevy campaign is someone else's code, and a
      // utm_campaign naming no active one ranks below the cookie: both are
      // left to the regular cookie/url path, where the cookie wins.
      var owned = !!offerSignal
        || (discountSignal ? eevyCampaignNamed(discountSignal, false) : (!!utmSignal && eevyCampaignNamed(utmSignal, true)));
      if (!owned) return null;

      var resolved = rt.resolveEffectiveBundleConfig(base, { context: context });
      return {
        selectedCode: resolved && resolved.campaign && resolved.campaign.code
          ? String(resolved.campaign.code).trim()
          : '',
        campaignKey: resolved && resolved.campaignKey || null
      };
    } catch (e) {
      return null;
    }
  }

  function resolve(explicitCode, options) {
    // Trace the caller so the user can see WHO triggered resolve in the
    // console (DOMContentLoaded handler, autoapply nudge -> refresh, manual
    // window.DentisDiscountEngine.refresh() call, etc).
    var callerHint = 'unknown';
    try {
      var stack = (new Error()).stack || '';
      if (stack.indexOf('refresh') !== -1) callerHint = 'refresh';
      else if (stack.indexOf('DOMContentLoaded') !== -1) callerHint = 'DOMContentLoaded';
      else if (stack.indexOf('resolve') !== -1) callerHint = 'direct/init';
    } catch (e) { /* ignore */ }
    DBG.t('RESOLVE', 'entry', { explicitCode: explicitCode || null, in_flight: !!RESOLVE_IN_FLIGHT, caller: callerHint });

    if (RESOLVE_IN_FLIGHT) {
      DBG.tw('RESOLVE-X', 'already in-flight; coalescing', { caller: callerHint });
      return RESOLVE_IN_FLIGHT;
    }

    var urlCode = readUrlParam();
    var cookieCode = readCookie(COOKIE_NAME);
    var trimmedExplicit = explicitCode ? String(explicitCode).trim() : '';
    var authoritativeExplicit = !!(options && options.authoritative === true);
    var freshEevyDecision = authoritativeExplicit ? null : readFreshEevyCampaignDecision();
    if (freshEevyDecision) {
      trimmedExplicit = freshEevyDecision.selectedCode;
      authoritativeExplicit = true;
    }
    // Cookie wins over explicit and url. The cookie reflects what Shopify
    // actually has on the cart session — when the customer arrived via
    // /discount/CAMPAIGN, the cookie is the campaign code. Callers passing
    // a different code (typically the autoapply snippet nudging with the
    // theme-settings storewide code) must not override that.
    var code = authoritativeExplicit ? trimmedExplicit : (cookieCode || trimmedExplicit || urlCode);
    DBG.t('RESOLVE', 'code-pick', {
      explicitCode: trimmedExplicit,
      authoritativeExplicit: authoritativeExplicit,
      freshEevyCampaignKey: freshEevyDecision && freshEevyDecision.campaignKey || null,
      urlCode: urlCode,
      cookieCode: cookieCode,
      picked: code
    });

    var staticCfg = readStaticPromoConfig();
    DBG.t('RESOLVE', 'static-config-loaded', {
      has_single: !!(staticCfg && staticCfg.single),
      single_code: staticCfg && staticCfg.single && staticCfg.single.code || null,
      has_campaigns: !!(staticCfg && staticCfg.campaigns),
      campaign_count: staticCfg && staticCfg.campaigns ? staticCfg.campaigns.length : 0,
      current: staticCfg && staticCfg.current || null
    });

    // Cores resolve from JSON regardless of any cookie. They paint as default
    // bundle-deal prices for the current PDP scope (e.g. DUO-PAKKEPRIS on the
    // Duo variant). Whether a core survives alongside the cookie's discount
    // is decided per-variant in price-override via the combinability matrix.
    var coreCampaigns = collectCoreCampaigns(staticCfg);
    if (coreCampaigns.length > 0) {
      DBG.t('CORES', 'matched ' + coreCampaigns.length + ' core campaign(s) for current PDP', {
        cores: coreCampaigns.map(function (c) { return c.code + (c.percentage ? '(' + c.percentage + '%)' : '(' + c.amount + 'kr)'); })
      });
    }

    if (!code) {
      // No cookie, no explicit, no URL. Before falling back to cores/inactive,
      // try cart-truth discovery: an Eevy campaign code may be applied to the
      // cart session even without a JS cookie on a plain PDP nav. If found,
      // resolve it natively through the campaign pipeline.
      var hasEevyIngested = staticCfg && Array.isArray(staticCfg.campaigns)
        && staticCfg.campaigns.some(function (c) { return c && c.eevy_bundle === true; });
      if (hasEevyIngested) {
        RESOLVE_IN_FLIGHT = discoverEevyCampaignCodeFromCart(staticCfg)
          .then(function (discovered) {
            RESOLVE_IN_FLIGHT = null;
            if (discovered) {
              var matched = matchCampaign(staticCfg, discovered);
              if (matched) return runCampaignFlow(discovered, matched, staticCfg, coreCampaigns);
            }
            // Nothing discovered — fall back to cores-only / inactive.
            if (coreCampaigns.length > 0) {
              var coresResolved = buildCoresOnlyResolved(coreCampaigns, staticCfg.current);
              dispatchResolvedOnce(coresResolved);
            } else {
              dispatchResolved({ active: false, code: null });
            }
          })
          .catch(function () {
            RESOLVE_IN_FLIGHT = null;
            dispatchResolved({ active: false, code: null });
          });
        return RESOLVE_IN_FLIGHT;
      }
      // No cookie, no explicit, no URL — but if cores match this PDP, paint
      // them as the resolved state. Empty inactive dispatch only when there's
      // truly nothing to apply.
      if (coreCampaigns.length > 0) {
        var coresOnlyResolved = buildCoresOnlyResolved(coreCampaigns, staticCfg.current);
        var coreCodesNoCookie = coreCampaigns.map(function (c) { return c.code; });
        DBG.t('RESOLVE', 'no cookie -> attaching cores then dispatching cores-only', { cores: coreCodesNoCookie });
        RESOLVE_IN_FLIGHT = getCart()
          .catch(function (err) { DBG.tw('CORES-P1', '/cart.js failed', { error: String(err) }); return null; })
          .then(function (cart1) {
            return attachCoresAndRestoreCookie(coreCodesNoCookie, null, cart1)
              .then(function () {
                var out = filterByCartTruth(coresOnlyResolved, cart1);
                dispatchResolvedOnce(out);
              });
          })
          .catch(function () { dispatchResolvedOnce(coresOnlyResolved); })
          .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
        return RESOLVE_IN_FLIGHT;
      }
      DBG.t('RESOLVE', 'no code anywhere, dispatching inactive');
      dispatchResolved({ active: false, code: null });
      return;
    }
    code = String(code).trim();
    if (!code) {
      if (coreCampaigns.length > 0) {
        var coresOnlyResolved2 = buildCoresOnlyResolved(coreCampaigns, staticCfg.current);
        var coreCodesEmptyCookie = coreCampaigns.map(function (c) { return c.code; });
        DBG.t('RESOLVE', 'empty cookie -> attaching cores then dispatching cores-only', { cores: coreCodesEmptyCookie });
        RESOLVE_IN_FLIGHT = getCart()
          .catch(function (err) { DBG.tw('CORES-P1', '/cart.js failed', { error: String(err) }); return null; })
          .then(function (cart1) {
            return attachCoresAndRestoreCookie(coreCodesEmptyCookie, null, cart1)
              .then(function () {
                var out = filterByCartTruth(coresOnlyResolved2, cart1);
                dispatchResolvedOnce(out);
              });
          })
          .catch(function () { dispatchResolvedOnce(coresOnlyResolved2); })
          .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
        return RESOLVE_IN_FLIGHT;
      }
      DBG.t('RESOLVE', 'code resolved to empty string, dispatching inactive');
      dispatchResolved({ active: false, code: null });
      return;
    }

    // Multi-campaign match: cookie code matches a configured campaign AND the
    // current PDP is in that campaign's scope (templates / product_handles /
    // variant_ids). Deterministic pipeline:
    //
    //   Phase 1: getCart() -> snapshot existing cart state (what codes are
    //            ALREADY on the session from prior pages / prior visits).
    //   Phase 2: From the snapshot, determine which expected codes (primary +
    //            children) are missing. Empty list = fast path, dispatch once
    //            from phase 1's cart and we're done. Otherwise...
    //   Phase 3: Fire /discount/<code>?redirect=/cart.js for each missing
    //            code in parallel, await all settlements.
    //   Phase 4: Re-read /cart.js to get the post-apply ground truth.
    //   Phase 5: Build the final resolved via filterByCartTruth and dispatch
    //            ONCE (dispatchResolvedOnce dedupes against the phase 1
    //            dispatch when the result is identical).
    //
    // No optimistic "paint now, correct later" — flicker eliminated.
    if (staticCfg && staticCfg.campaigns) {
      var matchedCampaign = matchCampaign(staticCfg, code);
      if (matchedCampaign) {
        return runCampaignFlow(code, matchedCampaign, staticCfg, coreCampaigns);
      } else {
        DBG.t('CAMPAIGN', 'no campaign matched current scope', {
          code: code,
          current_template: staticCfg.current && staticCfg.current.template_suffix,
          current_handle: staticCfg.current && staticCfg.current.product_handle,
          current_variants: staticCfg.current && staticCfg.current.product_variant_ids
        });
      }
    }

    if (staticCfg && staticCfg.single && staticCfg.single.code.toLowerCase() === code.toLowerCase()) {
      // Single = order-kind storewide. Combine with cores into a single
      // discounts[] payload so price-override can decide per-variant via
      // the combinability matrix whether to stack or override.
      var singleBase = buildResolvedFromStaticConfig(code, staticCfg.single);
      var subs = [];
      for (var ssi = 0; ssi < coreCampaigns.length; ssi++) {
        subs.push(buildSingleCampaignResolved(coreCampaigns[ssi].code, coreCampaigns[ssi], staticCfg.current));
      }
      subs.push(singleBase);
      var singleSynthesized = subs.length > 1
        ? Object.assign({}, singleBase, { discounts: subs, source: 'single+cores' })
        : singleBase;
      DBG.t('SINGLE', 'matched storewide single (+cores)', {
        single: staticCfg.single,
        core_count: coreCampaigns.length,
        synthesized: summarizeResolved(singleSynthesized)
      });
      RESOLVE_IN_FLIGHT = getCart()
        .catch(function (err) { DBG.tw('SINGLE-P1', '/cart.js failed', { error: String(err) }); return null; })
        .then(function (cart) {
          DBG.t('SINGLE-P1', 'snapshot', {
            item_count: cart ? (cart.item_count || (cart.items && cart.items.length) || 0) : null,
            discount_codes: cart && cart.discount_codes || null
          });
          var out = filterByCartTruth(singleSynthesized, cart);
          DBG.t('FILTER', 'single result', { input: summarizeResolved(singleSynthesized), output: summarizeResolved(out) });
          dispatchResolvedOnce(out);
        })
        .catch(function (err) {
          DBG.te('SINGLE', 'flow failed, dispatching synthesized as last-resort', { error: String(err) });
          dispatchResolvedOnce(singleSynthesized);
        })
        .then(function () { RESOLVE_IN_FLIGHT = null; DBG.t('RESOLVE-END', 'single flow finalized'); }, function () { RESOLVE_IN_FLIGHT = null; });
      return RESOLVE_IN_FLIGHT;
    }

    // Guard: a registered active Eevy bundle campaign code (or utm-alias) must
    // NEVER be treated as an influencer code. Normally collectEevyCampaigns()
    // already ingested it (including message/component-only campaigns), so this
    // branch is a race/ordering belt-and-suspenders guard (a9401f0). Fail-open
    // for non-Eevy codes and true influencer codes.
    if (isInfluencerCode(code) && isRegisteredEevyCampaignCode(code)) {
      DBG.tw('INFLUENCER-X', 'blocked influencer fallback: code is a registered active Eevy campaign', { code: code });
    }

    // Influencer pattern: cookie code follows `<LETTERS>20` convention.
    // Treated as a 20%-off order-level discount that combines with cores +
    // anything else. Stacks naturally with cores via the per-variant matrix
    // composer (cross-kind product+order, both combines all-true).
    if (isInfluencerCode(code) && !isRegisteredEevyCampaignCode(code)) {
      var coresForInfluencer = coreCampaigns.filter(function (c) {
        return String(c.code).toLowerCase().trim() !== code.toLowerCase();
      });
      var synthesizedInfluencer = buildInfluencerResolved(code, coresForInfluencer, staticCfg.current);
      var coreCodesForInfluencer = coresForInfluencer.map(function (c) { return c.code; });
      DBG.t('INFLUENCER', 'matched pattern (' + INFLUENCER_DEFAULT_PERCENTAGE + '% order-level)', {
        code: code,
        core_count: coresForInfluencer.length,
        core_codes: coreCodesForInfluencer,
        synthesized: summarizeResolved(synthesizedInfluencer)
      });

      RESOLVE_IN_FLIGHT = getCart()
        .catch(function (err) { DBG.tw('INFLUENCER-P1', '/cart.js failed', { error: String(err) }); return null; })
        .then(function (cart1) {
          DBG.t('INFLUENCER-P1', 'snapshot', {
            item_count: cart1 ? (cart1.item_count || (cart1.items && cart1.items.length) || 0) : null,
            discount_codes: cart1 && cart1.discount_codes || null
          });
          return attachCoresAndRestoreCookie(coreCodesForInfluencer, code, cart1)
            .then(function () {
              // No-op if no cores were attached; otherwise re-read cart to
              // get the post-attach truth before filtering.
              if (!coreCodesForInfluencer.length) return cart1;
              return getCart().catch(function (err) {
                DBG.tw('INFLUENCER-P2', '/cart.js re-read failed, falling back to pre-attach snapshot', { error: String(err) });
                return cart1;
              });
            })
            .then(function (cart2) {
              DBG.t('INFLUENCER-P2', 'post-attach snapshot', {
                item_count: cart2 ? (cart2.item_count || (cart2.items && cart2.items.length) || 0) : null,
                discount_codes: cart2 && cart2.discount_codes || null
              });
              var out = filterByCartTruth(synthesizedInfluencer, cart2);
              DBG.t('FILTER', 'influencer result', { input: summarizeResolved(synthesizedInfluencer), output: summarizeResolved(out) });
              dispatchResolvedOnce(out);
            });
        })
        .catch(function (err) {
          DBG.te('INFLUENCER', 'flow failed, dispatching synthesized as last-resort', { error: String(err) });
          dispatchResolvedOnce(synthesizedInfluencer);
        })
        .then(function () { RESOLVE_IN_FLIGHT = null; DBG.t('RESOLVE-END', 'influencer flow finalized'); }, function () { RESOLVE_IN_FLIGHT = null; });
      return RESOLVE_IN_FLIGHT;
    }

    // No static-config match for this code. If cores exist for the current
    // PDP, attach them to the cart session (firing /discount/ for any
    // missing core code) and dispatch cores-only paint. Skip the live
    // probe — the unknown cookie code is either a customer-entered code we
    // don't pre-configure (Shopify will still apply it server-side, and
    // the engine reflects what cart-truth reports) or a stale cookie.
    // Cores remain valid either way.
    if (coreCampaigns.length > 0) {
      var fallbackCoreCodes = coreCampaigns.map(function (c) { return c.code; });
      DBG.t('CORES', 'unknown code "' + code + '" -> attaching cores + dispatching cores-only', {
        cores: fallbackCoreCodes,
        restore_cookie_to: code
      });
      var fallbackCoresResolved = buildCoresOnlyResolved(coreCampaigns, staticCfg.current);
      RESOLVE_IN_FLIGHT = getCart()
        .catch(function (err) { DBG.tw('CORES-P1', '/cart.js failed', { error: String(err) }); return null; })
        .then(function (cart1) {
          return attachCoresAndRestoreCookie(fallbackCoreCodes, code, cart1)
            .then(function () {
              if (!fallbackCoreCodes.length) return cart1;
              return getCart().catch(function () { return cart1; });
            })
            .then(function (cart2) {
              var out = filterByCartTruth(fallbackCoresResolved, cart2);
              dispatchResolvedOnce(out);
            });
        })
        .catch(function () { dispatchResolvedOnce(fallbackCoresResolved); })
        .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
      return RESOLVE_IN_FLIGHT;
    }

    var cached = readCachedResult(code);
    if (cached) {
      DBG.log('cache HIT for code', code, cached);
      dispatchResolvedOnce(cached);
      return;
    }
    DBG.log('cache MISS for code', code, '- POST /cart/update.js');

    RESOLVE_IN_FLIGHT = applyDiscountToCart(code)
      .then(function (resp) {
        DBG.log('POST /cart/update.js response', resp);
        return resp;
      })
      .catch(function (err) {
        DBG.warn('POST /cart/update.js failed (non-fatal, code may still be saved on cart)', err);
      })
      .then(function () {
        DBG.log('GET /cart.js');
        return getCart();
      })
      .then(function (cart) {
        DBG.log('GET /cart.js response', cart);
        var resolved = buildResolved(code, cart);
        DBG.log('buildResolved output', resolved);
        // No probing. Adding/removing a probe variant fires Shopify cart:add
        // analytics (GA4 add_to_cart, Meta Pixel AddToCart, Klaviyo, etc.) and
        // pollutes performance metrics. Eligibility for product-level
        // (target_selection='entitled') discounts is only known when the cart
        // already contains an entitled line. For empty-cart scenarios the
        // engine relies on the static promo config short-circuit (see top of
        // resolve()) — merchants configure the discount once in theme settings
        // and the snippet renders pre-cart without any /cart/* round trips.
        return resolved;
      })
      .then(function (resolved) {
        if (resolved && resolved.active) {
          writeCachedResult(code, resolved);
          DBG.log('cached resolved discount in localStorage');
        } else {
          clearCache();
          DBG.log('no active discount; cleared cache');
        }
        dispatchResolvedOnce(resolved || { active: false, code: code });
      })
      .catch(function (err) {
        DBG.error('resolve() failed', err);
        clearCache();
        dispatchResolvedOnce({ active: false, code: code });
      })
      .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
    return RESOLVE_IN_FLIGHT;
  }

  window.DentisDiscountEngine = {
    resolve: resolve,
    refresh: function (explicitCode, options) { clearCache(); return resolve(explicitCode, options); },
    clearCache: clearCache,
    EVENT_NAME: EVENT_NAME
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resolve);
  } else {
    resolve();
  }

  // The Eevy runtime + base config publish asynchronously (deferred asset +
  // inline snippet). Re-resolve when they arrive or when the active campaign
  // changes so an ingested Eevy campaign is picked up regardless of load
  // ordering and the announcement re-renders on campaign switch. resolve() is
  // guarded by RESOLVE_IN_FLIGHT and dispatchResolvedOnce dedupes identical
  // results, so these are cheap and won't loop or flicker.
  if (typeof document !== 'undefined') {
    document.addEventListener('eevy:bundles-runtime-ready', function () { resolve(); });
    document.addEventListener('eevy:bundle-config-resolved', function () { resolve(); });
    // A reconcile that lands while a resolve is running would be coalesced
    // into that resolve and lost; it runs once after it instead, with the
    // latest reconciled code. One trailing resolve per reconcile event, so
    // this is bounded by the reconciles the bundle config snippet makes.
    var trailingCode = null;
    function refreshReconciled() {
      if (RESOLVE_IN_FLIGHT) {
        RESOLVE_IN_FLIGHT.then(refreshReconciled, refreshReconciled);
        return;
      }
      var code = trailingCode;
      trailingCode = null;
      if (code !== null) window.DentisDiscountEngine.refresh(code, { authoritative: true });
    }
    document.addEventListener('eevy:bundle-campaign-reconciled', function (event) {
      var detail = event && event.detail;
      if (!detail || detail.verified !== true || detail.error) return;
      var queued = trailingCode !== null;
      trailingCode = detail.selectedCode == null ? '' : String(detail.selectedCode);
      if (!queued) refreshReconciled();
    });
  }
})();
